import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { library, programs } from '@cwos/api-client';
import { Button } from '@cwos/ui-admin';
import { documentFormatOf, extractDocumentText } from './documentText.js';
import { DOCUMENT_KINDS, suggestDocumentKind } from './libraryLogic.js';

/**
 * ADD A DOCUMENT TO THE LIBRARY — step one of the pipeline.
 *
 * upload → parse/index → classify → propose readings → review → approve → in force.
 *
 * The whole affordance sits on ONE real endpoint, `POST /knowledge/sources`
 * (`library.uploadSource`), which takes a title and `sourceText` verbatim.
 *
 * ===========================================================================
 * THE FILE IS READ HERE, IN THE BROWSER
 * ===========================================================================
 * PDF (pdf.js), Word .docx (mammoth), .txt and .md are turned into text in this
 * tab (`documentText.ts`, both readers loaded only when such a file is dropped)
 * and only that text is sent; the file never leaves the browser. A scanned PDF
 * has no text layer and is refused in a sentence, with "Paste text" as the way
 * through. Once stored, the text is indexed for search at once.
 *
 * CLASSIFY: a suggested kind (`suggestDocumentKind`) is filled in and the
 * person keeps or changes it; it is saved through the existing classification
 * route straight after the upload.
 *
 * ===========================================================================
 * WHAT UPLOADING DOES NOT DO
 * ===========================================================================
 * It activates no policy. A rule exists only once a person proposes a reading
 * of the document (the next step, `ProposeReading`) and an approver accepts it
 * — the database refuses a rule without an approved reading behind it, and the
 * API refuses a proposer approving their own reading when anyone else could.
 */

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

type Mode = 'file' | 'paste';
type Phase = 'idle' | 'reading' | 'ready' | 'uploading' | 'failed';

export function UploadSource({
  onUploaded,
  onCancel,
}: {
  /** The stored source, and the text that was sent — for the propose step that follows. */
  onUploaded: (source: library.SourceView, text: string) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<Mode>('file');
  const [phase, setPhase] = useState<Phase>('idle');
  const [dragOver, setDragOver] = useState(false);
  const [refusedFile, setRefusedFile] = useState<string | null>(null);
  const [fileMeta, setFileMeta] = useState<{ name: string; size: number; pages?: number } | null>(
    null,
  );
  const [kind, setKind] = useState('');

  const [title, setTitle] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [versionLabel, setVersionLabel] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [programId, setProgramId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [supersedesSourceId, setSupersedesSourceId] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // For the "replaces an earlier document" picker and the program scope —
  // both real, existing, already-permitted reads.
  const programOptions = useQuery({
    queryKey: ['programs', 'options'],
    queryFn: () => programs.list({ status: 'active', limit: 200 }),
  });
  const existingSources = useQuery({
    queryKey: ['library', 'sources', ''],
    queryFn: () => library.sources({}),
  });

  const upload = useMutation({
    mutationFn: async () => {
      const source = await library.uploadSource({
        title: title.trim(),
        sourceText,
        ...(versionLabel.trim() ? { versionLabel: versionLabel.trim() } : {}),
        ...(jurisdiction.trim() ? { jurisdiction: jurisdiction.trim() } : {}),
        ...(programId ? { programId } : {}),
        ...(effectiveFrom ? { effectiveFrom } : {}),
        ...(supersedesSourceId ? { supersedesSourceId } : {}),
      });
      // CLASSIFY — the kind the person kept or chose. A failure here leaves a
      // stored, unclassified document, which the detail page can still fix.
      if (kind.trim() === '') return source;
      return library.classifySource(source.id, kind.trim()).catch(() => source);
    },
    onMutate: () => {
      setPhase('uploading');
      setErrorMessage(null);
    },
    onSuccess: (source) => {
      void queryClient.invalidateQueries({ queryKey: ['library'] });
      onUploaded(source, sourceText);
    },
    onError: (error) => {
      setPhase('failed');
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'The document could not be uploaded. Nothing was saved.',
      );
    },
  });

  async function readFile(file: File) {
    setRefusedFile(null);
    setErrorMessage(null);
    const name = file.name.replace(/\.[^.]+$/, '');
    if (documentFormatOf(file.name) === null) {
      setRefusedFile(
        `${file.name} is not a format Phibian can read yet. Use PDF, Word (.docx), .txt or .md — or open it, copy the text, and paste it below.`,
      );
      setMode('paste');
      if (title.trim() === '') setTitle(name);
      return;
    }
    setFileMeta({ name: file.name, size: file.size });
    setPhase('reading');
    const read = await extractDocumentText(file);
    if (!read.ok) {
      setFileMeta(null);
      setPhase('idle');
      setRefusedFile(read.message);
      return;
    }
    setSourceText(read.text);
    setFileMeta({ name: file.name, size: file.size, ...(read.pages ? { pages: read.pages } : {}) });
    const nextTitle = title.trim() === '' ? name : title;
    setTitle(nextTitle);
    if (kind.trim() === '') setKind(suggestDocumentKind(nextTitle, read.text) ?? '');
    setPhase('ready');
  }

  const canSubmit = title.trim() !== '' && sourceText.trim() !== '' && phase !== 'uploading';

  return (
    <form
      className="rn-col rn-card card-record kl-upload"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) upload.mutate();
      }}
    >
      <div className="rn-card-head">
        <h2 className="rn-card-title">Upload a document</h2>
        <button type="button" className="rn-btn-quiet" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <p className="rn-muted">
        This stores the document and makes it searchable. It changes nothing about what Phibian
        recommends until a person proposes a reading of it and an approver accepts that reading.
      </p>

      {/* THE FORMAT LIST, STATED BEFORE ANYONE PICKS A FILE. */}
      <div className="kl-formats" role="note">
        <p>
          <span className="rn-strong">Read directly, in your browser:</span> PDF, Word (.docx),
          plain text (.txt) and Markdown (.md). Only the text is sent; the file stays on your
          computer.
        </p>
        <p>
          <span className="rn-strong">Scanned PDFs and other formats:</span> a scan has no text to
          read, and older formats (.doc, .rtf, .pages) have no reader here. Open the file, copy the
          text, and paste it in below.
        </p>
      </div>

      <label className="rn-col kl-field">
        <span className="rn-strong">Title</span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Employee Handbook 2026"
          required
        />
      </label>

      {/* A two-way toggle, not a tablist: the content below is one field
          whose SOURCE changes, not a set of separate panels, so this uses
          the segmented-filter pattern (`aria-pressed`) the rest of the
          product already uses for the same shape of control. */}
      <div className="kl-mode-tabs" role="group" aria-label="How to add the content">
        <button
          type="button"
          aria-pressed={mode === 'file'}
          className="rn-tab-btn"
          onClick={() => setMode('file')}
        >
          Upload a file
        </button>
        <button
          type="button"
          aria-pressed={mode === 'paste'}
          className="rn-tab-btn"
          onClick={() => setMode('paste')}
        >
          Paste text
        </button>
      </div>

      {mode === 'file' ? (
        <div
          className="kl-dropzone"
          data-dragover={dragOver ? 'true' : undefined}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file) void readFile(file);
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.txt,.md,.markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
            className="kl-file-input"
            aria-label="Choose a document"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void readFile(file);
            }}
          />
          {phase === 'reading' ? (
            <p className="rn-muted">Reading {fileMeta?.name ?? 'the file'}…</p>
          ) : fileMeta ? (
            <div className="rn-col kl-file-picked">
              <span className="rn-strong">{fileMeta.name}</span>
              <span className="rn-muted">
                {formatBytes(fileMeta.size)}
                {fileMeta.pages ? ` · ${fileMeta.pages} pages` : ''} ·{' '}
                {sourceText.length.toLocaleString()} characters read
              </span>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setFileMeta(null);
                  setSourceText('');
                  setPhase('idle');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              >
                Choose a different file
              </Button>
            </div>
          ) : (
            <>
              <p className="rn-strong">Drag a file here, or</p>
              <Button
                type="button"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose a file
              </Button>
              <p className="rn-muted">PDF, Word (.docx), .txt or .md — read in your browser.</p>
            </>
          )}
          {refusedFile ? (
            <p className="rn-muted kl-refused" role="note">
              {refusedFile}
            </p>
          ) : null}
        </div>
      ) : (
        <label className="rn-col kl-field">
          <span className="rn-strong">Document text</span>
          <textarea
            className="kl-paste"
            value={sourceText}
            onChange={(e) => setSourceText(e.target.value)}
            placeholder="Paste the document’s text here — copied from a PDF, a Word document, or anywhere else."
            rows={12}
          />
          <span className="rn-muted">{sourceText.length.toLocaleString()} characters</span>
        </label>
      )}

      <div className="kl-meta-grid">
        <label className="rn-col kl-field">
          <span className="rn-strong">Kind of document</span>
          <input
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            list="kl-document-kinds"
            placeholder="Policy, Handbook, Procedure…"
          />
          <datalist id="kl-document-kinds">
            {DOCUMENT_KINDS.map((k) => (
              <option key={k} value={k} />
            ))}
          </datalist>
          <span className="rn-muted">
            {kind !== '' && kind === suggestDocumentKind(title, sourceText)
              ? 'Suggested from the title and opening text — change it if it is wrong.'
              : 'Optional. You can set it later.'}
          </span>
        </label>
        <label className="rn-col kl-field">
          <span className="rn-strong">Version label</span>
          <input
            value={versionLabel}
            onChange={(e) => setVersionLabel(e.target.value)}
            placeholder="2026 edition"
          />
        </label>
        <label className="rn-col kl-field">
          <span className="rn-strong">State or region</span>
          <input
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            placeholder="Not narrowed to one, if left blank"
          />
        </label>
        {/* Only when the organization HAS programs — never an empty picker. */}
        {(programOptions.data?.programs ?? []).length > 0 ? (
          <label className="rn-col kl-field">
            <span className="rn-strong">Program</span>
            <select value={programId} onChange={(e) => setProgramId(e.target.value)}>
              <option value="">Every program</option>
              {(programOptions.data?.programs ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="rn-col kl-field">
          <span className="rn-strong">Effective from</span>
          <input
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
          />
        </label>
        <label className="rn-col kl-field">
          <span className="rn-strong">Replaces</span>
          <select
            value={supersedesSourceId}
            onChange={(e) => setSupersedesSourceId(e.target.value)}
          >
            <option value="">Nothing — this is not a replacement</option>
            {(existingSources.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
                {s.versionLabel ? ` (${s.versionLabel})` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      {phase === 'failed' && errorMessage ? (
        <p className="rn-muted kl-error" role="alert">
          {errorMessage}
        </p>
      ) : null}

      <div className="rn-row kl-actions">
        <Button type="submit" variant="primary" disabled={!canSubmit}>
          {phase === 'uploading' ? 'Adding…' : 'Add and choose what it says'}
        </Button>
        <span className="rn-muted">
          Stored and searchable right away. Next you pick the passages that are rules; nothing is in
          force until an approver accepts them.
        </span>
      </div>
    </form>
  );
}
